import React, { useState, useEffect } from 'react';
import { Users, Phone, CheckCircle, Clock, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const OpsDashboard = () => {
  const [stats, setStats] = useState({
    totalClients: 0,
    pendingSetup: 0,
    inReview: 0,
    live: 0,
    pendingQA: 0
  });
  const [recentClients, setRecentClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('ops_token');
    
    try {
      const res = await fetch(`${API_URL}/api/ops/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        const clients = data.clients || [];
        
        // Calculate stats
        setStats({
          totalClients: clients.length,
          pendingSetup: clients.filter(c => c.onboarding_status === 'pending_setup').length,
          inReview: clients.filter(c => c.onboarding_status === 'in_review').length,
          live: clients.filter(c => c.onboarding_status === 'live').length,
          pendingQA: clients.filter(c => c.qa_status === 'pending' && c.onboarding_status !== 'pending_setup').length
        });
        
        // Get recent clients needing attention
        const needsAttention = clients
          .filter(c => c.onboarding_status !== 'live' && c.onboarding_status !== 'suspended')
          .slice(0, 5);
        setRecentClients(needsAttention);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const employee = JSON.parse(localStorage.getItem('ops_employee') || '{}');

  const statCards = [
    { label: 'Total Clients', value: stats.totalClients, icon: Users, color: 'bg-gray-100 text-gray-700' },
    { label: 'Pending Setup', value: stats.pendingSetup, icon: Clock, color: 'bg-yellow-100 text-yellow-700' },
    { label: 'In Review', value: stats.inReview, icon: AlertTriangle, color: 'bg-blue-100 text-blue-700' },
    { label: 'Live', value: stats.live, icon: CheckCircle, color: 'bg-green-100 text-green-700' },
  ];

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Welcome back, {employee.name}!</h1>
        <p className="text-gray-600 mt-1">Here's your onboarding overview</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{label}</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{loading ? '-' : value}</p>
              </div>
              <div className={`w-12 h-12 rounded-lg ${color} flex items-center justify-center`}>
                <Icon className="w-6 h-6" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pending QA Alert */}
      {stats.pendingQA > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8 flex items-center gap-4">
          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <p className="font-medium text-amber-800">
              {stats.pendingQA} client{stats.pendingQA > 1 ? 's' : ''} waiting for QA
            </p>
            <p className="text-sm text-amber-700">Complete QA checklist to activate them</p>
          </div>
        </div>
      )}

      {/* Clients Needing Attention */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Clients Needing Attention</h2>
        </div>
        
        {loading ? (
          <div className="p-6 text-center text-gray-500">Loading...</div>
        ) : recentClients.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
            <p>All caught up! No pending onboarding tasks.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {recentClients.map(client => (
              <div key={client.id} className="p-4 hover:bg-gray-50 flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">{client.name}</p>
                  <p className="text-sm text-gray-500">{client.industry} • {client.plan_id || 'No plan'}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    client.onboarding_status === 'pending_setup' ? 'bg-yellow-100 text-yellow-700' :
                    client.onboarding_status === 'in_review' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {client.onboarding_status}
                  </span>
                  {client.qa_status === 'pending' && (
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                      QA Pending
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        <a 
          href="/ops/clients" 
          className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow flex items-center gap-4"
        >
          <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
            <Users className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <p className="font-medium text-gray-900">View All Clients</p>
            <p className="text-sm text-gray-500">Manage onboarding</p>
          </div>
        </a>
        
        <a 
          href="/ops/link-agent" 
          className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow flex items-center gap-4"
        >
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
            <Phone className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <p className="font-medium text-gray-900">Link Agent</p>
            <p className="text-sm text-gray-500">Connect SigmaMind agent</p>
          </div>
        </a>
        
        <a 
          href="/ops/calls" 
          className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow flex items-center gap-4"
        >
          <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
            <Phone className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <p className="font-medium text-gray-900">Recent Calls</p>
            <p className="text-sm text-gray-500">View call history</p>
          </div>
        </a>
      </div>
    </div>
  );
};
